(ns ductile.ui.components.table
  (:require [cljs.reader :as reader]
            [clojure.set]
            [clojure.string :as str]
            [ductile.ui.components :as ui]
            [ductile.ui.components.omnibox :as omnibox]
            [ductile.ui.locale :as locale :refer [tr]]
            [nextjournal.clerk-table-stats :as table-stats]
            [nextjournal.clerk.render.hooks :as hooks])
  (:require-macros [ductile.ui.macros :refer [defc]]))

(defn col->data-attr [col]
  (cond-> col
    (sequential? col) flatten
    true pr-str))

(defn rect-overlap-bounds? [a {:keys [left right top bottom]}]
  (and (< (.-left a) right)
       (> (.-right a) left)
       (< (.-top a) bottom)
       (> (.-bottom a) top)))

(defn drag-rect [{:keys [start-x start-y x y]}]
  #js {:left (min start-x x)
       :top (min start-y y)
       :right (max start-x x)
       :bottom (max start-y y)})

(defn parse-width-number [v]
  (cond
    (number? v) v
    (string? v) (let [n (js/parseFloat v)]
                  (when-not (js/isNaN n) n))
    :else nil))

(defn column-layout-default-width [column-layout col-path]
  (when (ifn? column-layout)
    (let [layout (column-layout col-path)]
      (or (parse-width-number (:width layout))
          (parse-width-number (:min-width layout))))))

(defn cell-overflowed? [^js el]
  (and el
       (or (> (.-scrollWidth el) (.-clientWidth el))
           (> (.-scrollHeight el) (.-clientHeight el)))))

(defn next-sort-state [[old-k old-dir] k]
  (cond
    (not= old-k k) [k :asc]
    (= old-dir :asc) [k :desc]
    :else [k :asc]))

(defn hide-drag-overlay! [overlay-el]
  (when overlay-el
    (set! (.. overlay-el -style -display) "none")))

(defn show-drag-overlay! [overlay-el {:keys [start-x start-y x y]}]
  (when overlay-el
    (let [left (min start-x x)
          top (min start-y y)
          width (js/Math.abs (- x start-x))
          height (js/Math.abs (- y start-y))]
      (set! (.. overlay-el -style -display) "block")
      (set! (.. overlay-el -style -left) (str left "px"))
      (set! (.. overlay-el -style -top) (str top "px"))
      (set! (.. overlay-el -style -width) (str width "px"))
      (set! (.. overlay-el -style -height) (str height "px")))))

(defn clear-drag-row-highlights! [highlighted-rows]
  (doseq [^js row-el highlighted-rows]
    (set! (.. row-el -style -backgroundColor) "")
    (.removeAttribute row-el "data-drag-selected")))

(defn sync-drag-row-highlights! [!highlighted-rows row-boxes selected-keys]
  (let [selected-keys (set selected-keys)
        highlighted (or (.-current !highlighted-rows) #{})
        next-highlighted (->> row-boxes
                              (keep (fn [{:keys [key el]}]
                                      (when (and el (contains? selected-keys key))
                                        el)))
                              set)
        to-remove (clojure.set/difference highlighted next-highlighted)
        to-add (clojure.set/difference next-highlighted highlighted)]
    (doseq [^js row-el to-remove]
      (set! (.. row-el -style -backgroundColor) "")
      (.removeAttribute row-el "data-drag-selected"))
    (doseq [^js row-el to-add]
      (set! (.. row-el -style -backgroundColor) "rgb(254 243 199)")
      (.setAttribute row-el "data-drag-selected" "true"))
    (set! (.-current !highlighted-rows) next-highlighted)))

(defc apply-sticky-styles [opts {:keys [sticky-header? sticky-col? header-index col-index col]}]
  (cond-> opts
    (or sticky-header? sticky-col?) (update :style assoc :position :sticky)
    sticky-header? (-> (update :style assoc :z-index 3 :top 0)
                       (update :class (partial ui/class-vec "table-header-sticky")))
    sticky-col? (-> (update :style assoc :left 0 :z-index 2)
                    (update :class (partial ui/class-vec "table-column-sticky"))
                    (assoc :data-col (col->data-attr col))
                    (assoc :title (col->data-attr col)))
    (and sticky-header? sticky-col?) (->
                                      (update :style assoc
                                              :z-index 4
                                              :box-shadow "inset 0 1px 0 #e7ebee, inset 0 -1px 0 #e7ebee,
                                                          inset 1px 0 0 #e7ebee, inset -1px 0 0 #e7ebee"))))

(defn header-cell [{:keys [class vertical? sub-headers? nested? header-cell index !sorting col sticky-col? opts]}]
  (let [v (cond
            nested? (last header-cell)
            (vector? header-cell) (first header-cell)
            :else header-cell)
        k (if (and (not nested?) (vector? header-cell)) (first header-cell) header-cell)
        leaf-col-path (cond
                        (and nested? (vector? header-cell)) header-cell
                        (and (not nested?) (not (vector? header-cell))) header-cell
                        :else nil)
        sorted? (= k (first @!sorting))
        sortable? (or
                   (not sub-headers?)
                   (not (vector? header-cell))
                   nested?)
        {:keys [translated-keys column-layout number-col? filters update-filters! !expanded-at th-class th sticky-header?
                header-indicator-fn resizable-cols? resizable-col-style-fn nested-group-end-cols]
         :or {translated-keys {}}} opts
        indicator (when (ifn? header-indicator-fn)
                    (header-indicator-fn k))
        shadow-row? (and sticky-header?
                         (not filters)
                         (or
                          ;; single-row headers
                          (not sub-headers?)
                          ;; second header row cells
                          nested?
                          ;; first-row cells that span into the second row
                          (and sub-headers?
                               (not nested?)
                               (not (vector? header-cell)))))]
    ^{:key index}
    [(or th :th)
     (cond-> {:class (cond-> ["print:text-[10px] print:bg-transparent print:px-[5px] print:py-[2px] text-slate-600
                               text-[12px] font-normal px-2 py-[2px] bg-white first:rounded-md-tl last:rounded-md-r border-[#e7ebee]
                               text-center whitespace-nowrap align-middle relative select-none group"]
                       (or (not filters)
                           (and (not nested?)
                                (vector? header-cell))) (conj "border-b")
                       nested? (conj "border-r")
                       (not nested?) (conj "border-r last:border-r-0")
                       (and (ifn? number-col?)
                            (number-col? index)) (conj "text-right")
                       sortable? (conj #_"hover:bg-slate-50" "cursor-pointer")
                       vertical? (conj "rotate-180")
                       sticky-col? (conj "bg-white")
                       shadow-row? (conj "after:content-[''] after:pointer-events-none after:absolute after:left-0 after:right-0 after:top-full after:h-[3px] after:bg-[linear-gradient(to_bottom,rgba(0,0,0,0.05),rgba(0,0,0,0))]")
                       sorted? (conj "bg-[#fcfdfe] font-semibold")
                       class (conj class))
              :style (cond-> {}
                       vertical? (assoc :writing-mode "vertical-lr")
                       (and column-layout (column-layout k)) (merge (column-layout k))
                       (and leaf-col-path (ifn? resizable-col-style-fn)) (merge (resizable-col-style-fn leaf-col-path)))
              :data-header-leaf (when leaf-col-path "true")
              :data-col-path (when leaf-col-path (pr-str leaf-col-path))
              :data-resize-top (when leaf-col-path
                                 (if (and nested?
                                          (contains? (or nested-group-end-cols #{}) leaf-col-path))
                                   "full"
                                   "leaf"))
              :on-context-menu (when (and leaf-col-path (ifn? (:header-on-context-menu opts)))
                                 (fn [event]
                                   (.preventDefault event)
                                   (.stopPropagation event)
                                   ((:header-on-context-menu opts) leaf-col-path event)))
              :on-click (when sortable?
                          (fn [_]
                            (swap! !sorting next-sort-state k)))}

       true (apply-sticky-styles {:sticky-header? sticky-header?
                                  :sticky-col? sticky-col?
                                  :header-index (if nested? 1 0)
                                  :col col
                                  :col-index index})
       (and (not nested?) (vector? header-cell))      (assoc :col-span (count (first (rest header-cell))))
       (and sub-headers? (not (vector? header-cell))) (update :row-span (fnil inc 1)))

     [:span.px-1
      {:class th-class
       :title (pr-str :col col :sc? sticky-col?)}
      (get translated-keys v (tr v))]

     (when indicator
       [:button.absolute.z-10.border-0.p-0.m-0
        {:type "button"
         :title (or (:title indicator) (tr "Edit filters"))
         :data-testid (:data-testid indicator)
         :class "w-[11px] h-[11px]"
         :style {:right 0
                 :top 0}
         :on-click (fn [event]
                     (.preventDefault event)
                     (.stopPropagation event)
                     (when-let [f (:on-click indicator)]
                       (f event)))
         :on-mouse-enter (fn [event]
                           (when-let [f (:on-mouse-enter indicator)]
                             (f event)))
         :on-mouse-leave (fn [event]
                           (when-let [f (:on-mouse-leave indicator)]
                             (f event)))}
        [:div
         {:class "absolute w-[8px] h-[8px] bg-[#6900ff] hover:bg-[#852FFF] bg-no-repeat bg-center cursor-pointer transition-colors"
          :style {:right 0 :top 0}}]
        [:div
         {:class "w-[11px] h-[11px] absolute"
          :style {:background-image "url('/images/filtered-col-indicators.png')"
                  :background-size "11px 11px"
                  :background-position "right top"
                  :background-repeat "no-repeat"
                  :right -2
                  :top -1}}]])

     (cond
       sorted?
       [:span.absolute.right-1
        {:class "top-1/2 -translate-y-1/2"}
        [ui/icon {:size 12} (case (second @!sorting)
                              :asc :arrow-up
                              :desc :arrow-down
                              :arrow-up)]]

       sortable?
       [:span.absolute.right-1.opacity-0.group-hover:opacity-50.transition-all
        {:class "top-1/2 -translate-y-1/2"}
        [ui/icon {:size 12} :arrow-up]])

     nil]))

(defc header-filter-cell [{:keys [index k opts col sticky-col?]}]
  (let [{:keys [filters update-filters! expanded-at-update-in! !expanded-at column-layout sticky-header?]} opts
        resizable-col-style-fn (:resizable-col-style-fn opts)
        v (if (vector? k) (last k) k)]
    ^{:key (str k)}
    [(or (:th opts) :th)
     (-> {:class (cond-> "text-slate-600 text-[12px] font-normal px-2 py-[2px] bg-white first:rounded-md-tl last:rounded-md-r border-[#e7ebee] text-left whitespace-nowrap border-r last:border-r-0 border-b align-top after:content-[''] after:pointer-events-none after:absolute after:left-0 after:right-0 after:top-full after:h-[3px] after:bg-[linear-gradient(to_bottom,rgba(0,0,0,0.05),rgba(0,0,0,0))]"
                   sticky-header? (str " sticky top-0 z-20")
                   sticky-col? (str " sticky left-0 bg-white z-30"))
          :style (cond-> {}
                   (and column-layout (column-layout k)) (merge (column-layout k))
                   (ifn? resizable-col-style-fn) (merge (resizable-col-style-fn k)))
          :data-col-path (pr-str k)}
         (apply-sticky-styles {:sticky-header? sticky-header?
                               :sticky-col? sticky-col?
                               :header-index 2
                               :col col
                               :col-index index}))
     (when-some [[component choices-or-label opts :as filter] (get filters k)]
       (case component
         :checkbox
         (let [selected (get-in @!expanded-at [:filters k] false)]
           [:label.w-full.flex.items-center.gap-1.justify-center
            {:class ["h-[24px]"]}
            [omnibox/checkbox
             {:checked? selected
              :on-change (fn [_]
                           (if expanded-at-update-in!
                             (expanded-at-update-in! [:filters k] not)
                             (do
                               (swap! !expanded-at update-in [:filters v] not)
                               (update-filters! k filter (not selected)))))}]
            choices-or-label])
         :omnibox
         [omnibox/omnibox
          (merge opts
                 {:choices     choices-or-label
                  :filters     (get-in @!expanded-at [:filters k] ^{:filter :filter/omnibox} #{})
                  :set-filters #(if expanded-at-update-in!
                                  (expanded-at-update-in! [:filters] assoc k %)
                                  (do
                                    (swap! !expanded-at update :filters assoc k %)
                                    (when update-filters!
                                      (update-filters! k filter %))))
                  :data-testid (str "omnibox-" k)})]
         :search
         (let [[_ {:keys [on-change]}] filter]
           [omnibox/search
            (merge opts {:set-filters on-change})])))]))


(defn use-sticky-cols!
  "Makes the first n cols and their headers stick to the left and to each other;
   by offsetting all but first col from left by sum of widths of preceding cols.


   sticky-cols is a list of head paths: `[:vin [:destination :city]]`

   Caller needs to ensure sticky cols and their headers have a background color set.

   Observes and auto-updates offsets whenever any column resizes or the table structure is mutated."
  [!table-ref sticky-cols]
  (hooks/use-layout-effect
   (fn []
     (let [debug? false
           update-sticky-cols!
           (fn update-sticky-cols! []
             (when (and @!table-ref (seq sticky-cols))
               (let [col-widths (doall
                                 (reduce (fn [acc col]
                                           ;; measure width of col
                                           (when-let [^js th (.querySelector @!table-ref (str "th.table-column-sticky[data-col=\"" (col->data-attr col) "\"]"))]
                                             (when debug? (set! (.. th -style -backgroundColor) "lime"))
                                             (assoc acc col {:width (.-width (.getBoundingClientRect th)) ; sum widths of preceding cols
                                                             :left (apply + (map :width (vals acc)))})))
                                         {} sticky-cols))]
                 (doseq [col sticky-cols]
                   (.. @!table-ref
                       (querySelectorAll (str ".table-column-sticky[data-col=\"" (col->data-attr col) "\"]"))
                       (forEach (fn [^js th]
                                  (when debug? (set! (.. th -style -backgroundColor) "pink"))
                                  (set! (.. th -style -left) (str (get-in col-widths [col :left]) "px")))))))))
           ^js resize-observer (js/ResizeObserver. update-sticky-cols!)
           ^js mutation-observer (js/MutationObserver. update-sticky-cols!)]

       (when (and @!table-ref (seq sticky-cols)
                  (.querySelector ^js @!table-ref "th.table-column-sticky:nth-child(1)"))
         (update-sticky-cols!)
         (.observe mutation-observer @!table-ref #js {:subtree true :childList true})
         (doseq [col sticky-cols]
           (.observe resize-observer (.querySelector ^js @!table-ref (str "th.table-column-sticky[data-col=\"" (col->data-attr col) "\"]")))))
       #(do ; TODO revert left offset on unmount
          (.disconnect mutation-observer @!table-ref)
          (.disconnect resize-observer))))
   [@!table-ref sticky-cols]))

(defn use-sticky-headers!
  [!table-ref]
  (hooks/use-layout-effect
   (fn []
     (let [debug? false
           sticky-headers-count (.. @!table-ref (querySelectorAll "thead>tr") -length)
           update-sticky-headers!
           (fn update-sticky-headers! []
             (when (and @!table-ref sticky-headers-count (< 1 sticky-headers-count))
               (let [!header-heights (atom [])]
                 (loop [i 0]
                   (when (< i sticky-headers-count)
                     (let [^js th (.querySelector @!table-ref (str "thead>tr:nth-child(" (inc i) ")"))]
                       (when debug? (set! (.. th -style -backgroundColor) "lime"))
                       (swap! !header-heights conj (when th (js/Math.round (.-height (.getBoundingClientRect th)))))
                       (recur (inc i)))))

                 (loop [i 0]
                   (when (< i sticky-headers-count)
                     (let [preceding-headers-height-sum (apply + (take i @!header-heights))
                           top-offset preceding-headers-height-sum]
                       (.. @!table-ref
                           (querySelectorAll (str "thead>tr:nth-child(" (inc i) ") th"))
                           (forEach (fn [^js th]
                                      (when debug? (set! (.. th -style -backgroundColor) "pink"))
                                      (set! (.. th -style -top) (str top-offset "px"))))))
                     (recur (inc i)))))))
           ^js resize-observer (js/ResizeObserver. update-sticky-headers!)
           ^js mutation-observer (js/MutationObserver. update-sticky-headers!)]

       (when (and @!table-ref (< 1 sticky-headers-count))
         (update-sticky-headers!)
         (.observe mutation-observer @!table-ref #js {:subtree true :childList true})
         (doseq [i (range sticky-headers-count)]
           (.observe resize-observer (.querySelector ^js @!table-ref (str "thead>tr:nth-child(" (inc i) ") th"))))
         (doseq [tr (js->clj (.querySelectorAll ^js @!table-ref "tbody>tr"))] ; offset sticky header height when scrolling rows into view
           (set! (.. tr -style -scrollMarginTop) (str (+ 30 (.. @!table-ref (querySelector "thead") (getBoundingClientRect) -height)) "px"))))
       #(do
          (.disconnect mutation-observer @!table-ref)
          (.disconnect resize-observer))))
   [@!table-ref]))


(defc table
  "Intermediary, faster replacement for Clerk tables with additional features like sorting & visibility."
  [{:as opts :keys [sticky-header?] :or {sticky-header? true}} data]
  (let [opts (assoc opts :sticky-header? sticky-header?)
        !table-container-ref (hooks/use-ref)
        !table-ref (hooks/use-ref)
        !table-clone-ref (hooks/use-ref)
        !sorting (or (:!sorting opts)
                     (hooks/use-state (:default-sorting opts)))
        !scroll-top (hooks/use-state 0)
        !viewport-height (hooks/use-state 0)
        !measured-row-height (hooks/use-state nil)
        !drag-state (hooks/use-ref nil)
        !drag-preview-raf-id (hooks/use-ref nil)
        !drag-pending-coords (hooks/use-ref nil)
        !drag-highlighted-rows (hooks/use-ref #{})
        !drag-overlay-ref (hooks/use-ref nil)
        !dragging? (hooks/use-state false)
        !baseline-col-widths (hooks/use-state {})
        !resizable-col-widths (hooks/use-state {})
        !hovered-overflow-cell (hooks/use-state nil)
        !col-resize-handles (hooks/use-state {})
        !active-resize-col (hooks/use-state nil)
        {:keys [head rows visible-paths] :as table} (table-stats/normalize-seq-of-map
                                                     (cond-> (dissoc opts :row-selected?)
                                                       (and (empty? data) ; avoid header disappearing while typing in filter, when server-filtered data is empty
                                                            (not (:schema opts))
                                                            (:column-order opts))
                                                       (assoc :schema (:column-order opts)))
                                                     data)
        {:keys [column-layout hide-head? filters row-action row-double-action row-on-context-menu sorting-columns selected-rows !expanded-at th-class td-class tr-class sticky-header? sticky-cols-count row-action-with-event? row-double-action-with-event? drag-select? on-drag-select on-drag-select-preview on-drag-select-commit resizable-cols?]} opts
        sticky-cols-count (or sticky-cols-count 0)

        separate-headers (fn [paths]
                           (mapv (fn [[parent child]] (if child [parent [child]] parent)) paths))
        merge-headers (fn [paths]
                        (->> paths
                             (partition-by first)
                             (mapv (fn [group]
                                     (if (= [nil] (mapv second group))
                                       (ffirst group)
                                       [(ffirst group) (mapv second group)])))))

        sticky-cols (separate-headers (take sticky-cols-count visible-paths))
        sticky-col? (partial (comp (set (map col->data-attr sticky-cols)) col->data-attr))
        head (concat
              sticky-cols
              (merge-headers (drop sticky-cols-count visible-paths)))
        nested-group-end-cols (->> head
                                   (keep (fn [h]
                                           (when (and (vector? h)
                                                      (sequential? (second h)))
                                             (when-let [last-child (last (second h))]
                                               [(first h) last-child]))))
                                   set)


        row-key-fn (or (:row-key opts) hash)
        selected-rows (set selected-rows)
        selected-row-keys (into #{}
                                (map (fn [row-or-key]
                                       (if (map? row-or-key)
                                         (row-key-fn row-or-key)
                                         row-or-key)))
                                selected-rows)
        col-paths (when head
                    (mapcat (fn [h]
                              (if (vector? h)
                                (map (fn [k] [(first h) k]) (last h))
                                [h]))
                            head))
        default-col-widths (when resizable-cols?
                             (reduce (fn [acc col-path]
                                       (if-let [w (column-layout-default-width column-layout col-path)]
                                         (assoc acc col-path w)
                                         acc))
                                     {}
                                     col-paths))
        resizable-col-style-fn (fn [col-path]
                                 (when (and resizable-cols? col-path)
                                   (when-let [w (or (get @!resizable-col-widths col-path)
                                                    (get default-col-widths col-path)
                                                    (get @!baseline-col-widths col-path))]
                                     {:width w
                                      :min-width w
                                      :max-width w})))
        start-col-resize!
        (fn [^js event col-path]
          (when (= 0 (.-button event))
            (.preventDefault event)
            (.stopPropagation event)
            (reset! !active-resize-col col-path)
            (let [th-el (.closest (.-target event) "th")
                  th-width (when th-el (.-width (.getBoundingClientRect th-el)))
                  start-width (double (or (get @!resizable-col-widths col-path)
                                          (get default-col-widths col-path)
                                          (get @!baseline-col-widths col-path)
                                          th-width
                                          100))
                  start-x (.-clientX event)
                  min-width 48]
              (letfn [(on-move [^js move-event]
                        (let [next-width (max min-width (+ start-width (- (.-clientX move-event) start-x)))]
                          (swap! !resizable-col-widths assoc col-path next-width)))
                      (on-up [_]
                        (set! (.. js/document -body -style -cursor) "")
                        (set! (.. js/document -body -style -userSelect) "")
                        (reset! !active-resize-col nil)
                        (.removeEventListener js/window "mousemove" on-move)
                        (.removeEventListener js/window "mouseup" on-up))]
                (set! (.. js/document -body -style -cursor) "col-resize")
                (set! (.. js/document -body -style -userSelect) "none")
                (.addEventListener js/window "mousemove" on-move)
                (.addEventListener js/window "mouseup" on-up)))))
        reset-col-size! (fn [col-path]
                          (if-let [default-width (get default-col-widths col-path)]
                            (swap! !resizable-col-widths assoc col-path default-width)
                            (swap! !resizable-col-widths dissoc col-path)))
        sorted-rows (if-some [[path-or-key dir] @!sorting]
                      (let [path   (if (vector? path-or-key) path-or-key [path-or-key])
                            key-fn (fn [row]
                                     (let [value (get-in (:data (meta row)) path)
                                           custom-sorting-fn (get sorting-columns path)]
                                       (cond
                                         custom-sorting-fn (custom-sorting-fn value)
                                         (string? value) (str/lower-case value)
                                         (number? value) value
                                         (nil? value) value
                                         (inst? value) (.getTime value)
                                         :else (str/lower-case (str value)))))]
                        (cond->> rows
                          true          (sort-by key-fn)
                          (= :desc dir) (reverse)))
                      rows)
        sorted-path (when-some [[path-or-key _] @!sorting]
                      (if (vector? path-or-key) path-or-key [path-or-key]))
        sorted-rows (vec sorted-rows)
        row-height (double (or @!measured-row-height
                               (:virtual-row-height opts)
                               30))
        overscan (or (:virtual-overscan opts) 20)
        virtualize? (and (:virtualize-rows? opts) (pos? row-height))
        total-rows (count sorted-rows)
        explicit-selector (:virtual-scroll-container-selector opts)
        scroller-now (when @!table-ref
                       (or (when (and explicit-selector (string? explicit-selector))
                             (.closest ^js @!table-ref explicit-selector))
                           js/window))
        viewport-height (max 0 (if (and virtualize? scroller-now)
                                 (if (identical? scroller-now js/window)
                                   (.-innerHeight js/window)
                                   (.-clientHeight scroller-now))
                                 @!viewport-height))
        scroll-top (max 0 (if (and virtualize? scroller-now)
                            (if (identical? scroller-now js/window)
                              (.-scrollY js/window)
                              (.-scrollTop scroller-now))
                            @!scroll-top))
        visible-count (if virtualize?
                        (max 1 (+ (int (js/Math.ceil (/ (max row-height viewport-height) row-height)))
                                  (* 2 overscan)))
                        total-rows)
        max-start-index (max 0 (- total-rows visible-count))
        start-index (if virtualize?
                      (-> (int (js/Math.floor (/ scroll-top row-height)))
                          (- overscan)
                          (max 0)
                          (min max-start-index))
                      0)
        end-index (if virtualize?
                    (min total-rows (+ start-index visible-count))
                    total-rows)
        visible-rows (if virtualize?
                       (subvec sorted-rows start-index end-index)
                       sorted-rows)
        top-spacer-height (if virtualize? (* start-index row-height) 0)
        bottom-spacer-height (if virtualize? (* (- total-rows end-index) row-height) 0)
        dragging? @!dragging?
        hovered-overflow-cell @!hovered-overflow-cell
        render-opts (assoc opts
                           :resizable-cols? resizable-cols?
                           :resizable-col-style-fn resizable-col-style-fn
                           :nested-group-end-cols nested-group-end-cols)]

    (use-sticky-cols! !table-ref sticky-cols)
    (use-sticky-headers! !table-ref)
    (hooks/use-layout-effect
     (fn []
       (when (and resizable-cols? @!table-ref)
         (js/requestAnimationFrame
          (fn []
            (when-let [^js table-el @!table-ref]
              (let [ths (array-seq (.querySelectorAll table-el "thead th[data-header-leaf='true'][data-col-path]"))
                    measured (reduce (fn [acc ^js th]
                                       (let [raw (.getAttribute th "data-col-path")
                                             width (.-width (.getBoundingClientRect th))]
                                         (try
                                           (let [col-path (reader/read-string raw)]
                                             (if (and col-path (pos? width))
                                               (assoc acc col-path width)
                                               acc))
                                           (catch :default _
                                             acc))))
                                     {}
                                     ths)]
                (when (seq measured)
                  (swap! !baseline-col-widths
                         (fn [existing]
                           (reduce-kv (fn [acc k v]
                                        (if (contains? acc k)
                                          acc
                                          (assoc acc k v)))
                                      existing
                                      measured)))))))))
       nil)
     [resizable-cols? @!table-ref col-paths])
    (hooks/use-layout-effect
     (fn []
       (when (and resizable-cols? @!table-ref @!table-container-ref)
         (let [update-handle-positions!
               (fn []
                 (when (and @!table-ref @!table-container-ref)
                   (let [^js container-rect (.getBoundingClientRect @!table-container-ref)
                         ^js thead-el (.querySelector ^js @!table-ref "thead")
                         header-top (if thead-el
                                      (js/Math.round (- (.-top (.getBoundingClientRect thead-el))
                                                        (.-top container-rect)))
                                      0)
                         ths (array-seq (.querySelectorAll ^js @!table-ref "thead th[data-header-leaf='true'][data-col-path]"))
                         handles (reduce (fn [acc ^js th]
                                           (let [raw (.getAttribute th "data-col-path")
                                                 resize-top-mode (.getAttribute th "data-resize-top")
                                                 ^js rect (.getBoundingClientRect th)
                                                 x (js/Math.round (- (.-right rect) (.-left container-rect)))
                                                 top (if (= "full" resize-top-mode)
                                                       header-top
                                                       (js/Math.round (- (.-top rect) (.-top container-rect))))]
                                             (try
                                               (let [col-path (reader/read-string raw)]
                                                 (if (and col-path (number? x) (number? top))
                                                   (assoc acc col-path {:x x :top (max 0 top)})
                                                   acc))
                                               (catch :default _
                                                 acc))))
                                         {}
                                         ths)]
                     (when (not= @!col-resize-handles handles)
                       (reset! !col-resize-handles handles)))))
               ^js resize-observer (js/ResizeObserver. update-handle-positions!)
               ^js mutation-observer (js/MutationObserver. update-handle-positions!)]
           (update-handle-positions!)
           (.observe resize-observer @!table-ref)
           (.observe mutation-observer @!table-ref #js {:subtree true :childList true})
           (.addEventListener js/window "resize" update-handle-positions!)
           (fn []
             (.disconnect resize-observer)
             (.disconnect mutation-observer)
             (.removeEventListener js/window "resize" update-handle-positions!))))
       nil)
     [resizable-cols? @!table-ref @!table-container-ref col-paths @!baseline-col-widths @!resizable-col-widths])
    (hooks/use-layout-effect
     (fn []
       (when (and virtualize? @!table-ref (nil? @!measured-row-height))
         (js/requestAnimationFrame
          (fn []
            (when-let [^js table-el @!table-ref]
              (when-let [^js row (.querySelector table-el "tbody tr[data-testid^='table-row-']")]
                (let [h (.-height (.getBoundingClientRect row))]
                  (when (and (number? h) (pos? h))
                    (reset! !measured-row-height h))))))))
       nil)
     [virtualize? @!table-ref])
    (hooks/use-layout-effect
     (fn []
       (when (and virtualize? @!table-ref)
         (let [scrollable? (fn [^js el]
                             (let [^js style (js/getComputedStyle el)
                                   overflow-y (.-overflowY style)
                                   overflow (.-overflow style)
                                   scroll-style? (fn [v]
                                                   (or (= v "auto")
                                                       (= v "scroll")
                                                       (= v "overlay")))]
                               (or (scroll-style? overflow-y)
                                   (scroll-style? overflow))))
               ancestors (loop [node (.-parentElement @!table-ref)
                                acc []]
                           (if (nil? node)
                             acc
                             (recur (.-parentElement node) (conj acc node))))
               explicit-scroller (when (and explicit-selector (string? explicit-selector))
                                   (.closest ^js @!table-ref explicit-selector))
               scroller (or explicit-scroller
                            (first (filter scrollable? ancestors))
                            js/window)
               update-state! (fn []
                               (let [^js table-el @!table-ref]
                                 (if (identical? scroller js/window)
                                   (let [^js table-rect (.getBoundingClientRect table-el)
                                         table-local-scroll (max 0 (- (.-top table-rect)))
                                         next-viewport-height (.-innerHeight js/window)]
                                     (when (> (js/Math.abs (- table-local-scroll @!scroll-top)) 0.5)
                                       (reset! !scroll-top table-local-scroll))
                                     (when (not= next-viewport-height @!viewport-height)
                                       (reset! !viewport-height next-viewport-height)))
                                   (let [^js scroller-rect (.getBoundingClientRect scroller)
                                         table-local-scroll (max 0 (.-scrollTop scroller))
                                         next-viewport-height (.-height scroller-rect)]
                                     (when (> (js/Math.abs (- table-local-scroll @!scroll-top)) 0.5)
                                       (reset! !scroll-top table-local-scroll))
                                     (when (not= next-viewport-height @!viewport-height)
                                       (reset! !viewport-height next-viewport-height))))))
               on-scroll! (fn [_]
                            (update-state!))
               on-resize! (fn []
                            (update-state!))]
           (update-state!)
           (.addEventListener scroller "scroll" on-scroll!)
           (.addEventListener js/window "resize" on-resize!)
           #(do
              (.removeEventListener scroller "scroll" on-scroll!)
              (.removeEventListener js/window "resize" on-resize!)))))
     [virtualize? @!table-ref])

    (hooks/use-effect
     (fn []
       (when dragging?
         (let [collect-row-keys (fn [rect row-boxes]
                                  (->> row-boxes
                                       (keep (fn [row-box]
                                               (when (rect-overlap-bounds? rect row-box)
                                                 (:key row-box))))
                                       set))
               run-selection-update! (fn [drag-state]
                                       (let [rect (drag-rect drag-state)
                                             row-keys (collect-row-keys rect (:row-boxes drag-state))
                                             changed? (not= row-keys (:row-keys drag-state))
                                             next-drag (assoc drag-state :row-keys row-keys)]
                                         (set! (.-current !drag-state) next-drag)
                                         (show-drag-overlay! @!drag-overlay-ref next-drag)
                                         (sync-drag-row-highlights! !drag-highlighted-rows (:row-boxes next-drag) row-keys)
                                         (when (and changed? (:on-preview next-drag))
                                           ((:on-preview next-drag) {:row-keys row-keys
                                                                     :append? (:append? drag-state)}))))
               ensure-frame! (fn []
                               (when-not (.-current !drag-preview-raf-id)
                                 (set! (.-current !drag-preview-raf-id)
                                       (js/requestAnimationFrame
                                        (fn []
                                          (set! (.-current !drag-preview-raf-id) nil)
                                          (when-let [{:keys [x y]} (.-current !drag-pending-coords)]
                                            (set! (.-current !drag-pending-coords) nil)
                                            (when-let [drag1 (.-current !drag-state)]
                                              (run-selection-update! (assoc drag1 :x x :y y :active? true)))))))))
               on-move (fn [^js event]
                         (when-let [drag0 (.-current !drag-state)]
                           (let [next-x (.-clientX event)
                                 next-y (.-clientY event)
                                 moved? (> (+ (js/Math.abs (- next-x (:start-x drag0)))
                                              (js/Math.abs (- next-y (:start-y drag0))))
                                           3)
                                 active? (or (:active? drag0) moved?)]
                             (when active?
                               (.preventDefault event)
                               (set! (.-current !drag-pending-coords) {:x next-x :y next-y})
                               (ensure-frame!)))))
               on-up (fn [_event]
                       (when-let [drag' (.-current !drag-state)]
                         (let [drag'' (if-let [{:keys [x y]} (.-current !drag-pending-coords)]
                                        (assoc drag' :x x :y y)
                                        drag')
                               row-keys (collect-row-keys (drag-rect drag'') (:row-boxes drag''))]
                           (show-drag-overlay! @!drag-overlay-ref drag'')
                           (when-let [on-drag-select (:on-commit drag'')]
                             (when (:active? drag'')
                               (on-drag-select {:row-keys row-keys
                                                :append? (:append? drag'')})))))
                       (when-let [frame-id (.-current !drag-preview-raf-id)]
                         (js/cancelAnimationFrame frame-id))
                       (hide-drag-overlay! @!drag-overlay-ref)
                       (clear-drag-row-highlights! (.-current !drag-highlighted-rows))
                       (set! (.-current !drag-preview-raf-id) nil)
                       (set! (.-current !drag-pending-coords) nil)
                       (set! (.-current !drag-highlighted-rows) #{})
                       (set! (.-current !drag-state) nil)
                       (reset! !dragging? false))]
           (js/window.addEventListener "mousemove" on-move)
           (js/window.addEventListener "mouseup" on-up)
           #(do
              (when-let [frame-id (.-current !drag-preview-raf-id)]
                (js/cancelAnimationFrame frame-id))
              (hide-drag-overlay! @!drag-overlay-ref)
              (clear-drag-row-highlights! (.-current !drag-highlighted-rows))
              (set! (.-current !drag-highlighted-rows) #{})
              (js/window.removeEventListener "mousemove" on-move)
              (js/window.removeEventListener "mouseup" on-up)))))
     [dragging?])

    [:div.relative
     {:ref !table-container-ref
      :on-mouse-down (fn [^js event]
                       (when (and drag-select?
                                  (= 0 (.-button event))
                                  (not (.closest (.-target event) "button,a,input,textarea,select"))
                                  (not (.closest (.-target event) "[data-col-resize-handle]")))
                         (let [row-boxes (if @!table-ref
                                           (->> (.querySelectorAll ^js @!table-ref "tbody tr[data-row-key]")
                                                array-seq
                                                (keep (fn [^js row-el]
                                                        (when-let [row-key-raw (.getAttribute row-el "data-row-key")]
                                                          (try
                                                            (let [rect (.getBoundingClientRect row-el)]
                                                              {:key (reader/read-string row-key-raw)
                                                               :el row-el
                                                               :left (.-left rect)
                                                               :top (.-top rect)
                                                               :right (.-right rect)
                                                               :bottom (.-bottom rect)})
                                                            (catch :default _ nil)))))
                                                vec)
                                           [])]
                           (set! (.-current !drag-state) {:active? false
                                                          :append? (or (.-ctrlKey event) (.-metaKey event))
                                                          :on-preview (or on-drag-select-preview on-drag-select)
                                                          :on-commit (or on-drag-select-commit on-drag-select)
                                                          :start-x (.-clientX event)
                                                          :start-y (.-clientY event)
                                                          :x (.-clientX event)
                                                          :y (.-clientY event)
                                                          :row-boxes row-boxes
                                                          :row-keys #{}})
                           (hide-drag-overlay! @!drag-overlay-ref)
                           (clear-drag-row-highlights! (.-current !drag-highlighted-rows))
                           (set! (.-current !drag-highlighted-rows) #{})
                           (reset! !dragging? true))))}
     [:table.sans-serif.text-gray-900.dark:text-white.not-prose.border-separate.border-spacing-0
      {:class (:class opts)
       :ref !table-ref
       :style (merge (:style opts)
                     (cond-> {}
                       virtualize? (assoc :overflow-anchor "none")
                       resizable-cols? (assoc :table-layout "fixed"
                                              :width "max-content")))}
      (when (and head (not hide-head?))
        (let [sub-headers (remove nil? (mapcat (fn [h] (when (vector? h)
                                                         (map (fn [k] [(first h) k]) (second h)))) head))]
          [:thead
           [:tr.print:border-b-2.print:border-black
            (map-indexed
             (fn [i cell]
               ^{:key (str "header-cell-" cell)}
               [header-cell {:sub-headers? (seq sub-headers)
                             :header-cell cell
                             :th-class th-class
                             :opts render-opts
                             :index i
                             :col cell
                             :sticky-col? (sticky-col? cell)
                             :!sorting !sorting}])
             head)]
           (when-not (empty? sub-headers)
             [:tr.print:border-b-2.print:border-black
              (map-indexed
               (fn [i cell]
                 ^{:key (str "sub-header-cell-" cell)}
                 [header-cell {:header-cell cell
                               :opts render-opts
                               :th-class th-class
                               :nested? true
                               :index i
                               :col cell
                               :sticky-col? (sticky-col? cell)
                               :!sorting !sorting}])
               sub-headers)])
           (when filters
             [:tr.print:border-b-2.print:border-black
              (for [[i cell] (map-indexed vector head)
                    key  (if (vector? cell)
                           (map #(vector (first cell) %) (second cell))
                           [cell])]
                (header-filter-cell {:k key :index i :opts render-opts
                                     :col cell
                                     :sticky-col? (sticky-col? cell)}))])]))
      (into [:tbody]
            (let [render-row (fn [idx row]
                               (let [row-index (+ start-index idx)
                                     row-data (:data (meta row))
                                     row-key (row-key-fn row-data)
                                     selected? (or (contains? selected-rows row-data)
                                                   (contains? selected-row-keys row-key))]
                                 ^{:key (str row-key)}
                                 (if (:ui.table/group-header row-data)
                                   [:tr
                                    [:td {:col-span (count row)}
                                     (:ui.table/group-header row-data)]]
                                   (into [:tr.border-b.print:border-b-gray-500.print:hover:bg-transparent
                                          {:data-testid (or (when (ifn? (:row-testid-fn opts))
                                                              ((:row-testid-fn opts) row-data))
                                                            (str "table-row-" row-index))
                                           :data-row-key (pr-str row-key)
                                           :class ["print:border-b-[1px]"
                                                   (when drag-select? "select-none")
                                                   (when row-action "cursor-pointer")
                                                   (if selected?
                                                     "bg-amber-100 hover:bg-amber-200 border-b-[#e7ebee]"
                                                     "hover:bg-indigo-50 odd:bg-[#fbfcfd] even:bg-white border-b-[#e7ebee]")
                                                   tr-class]
                                           :on-click (fn [event]
                                                       (when row-action
                                                         (if row-action-with-event?
                                                           (row-action row-data event)
                                                           (row-action row-data))))
                                           :on-double-click (fn [event]
                                                              (when row-double-action
                                                                (if row-double-action-with-event?
                                                                  (row-double-action row-data event)
                                                                  (row-double-action row-data))))
                                           :on-context-menu (fn [event]
                                                              (when row-on-context-menu
                                                                (row-on-context-menu row-data event)))}]
                                         (map-indexed
                                          (fn [i cell]
                                            (let [col-path (nth col-paths i)
                                                  sorted-col? (= col-path sorted-path)
                                                  expanded-overflow? (= hovered-overflow-cell [row-key col-path])]
                                              ^{:key (str "row-" row-index "-cell-" i)}
                                              [:td.px-2.py-1.border-r.last:border-r-0.whitespace-nowrap
                                               (-> {:data-col-path (pr-str col-path)
                                                    :class ["text-[12px] print:text-[10px] bg-inherit print:bg-transparent print:px-[5px] print:py-[2px]"
                                                            (when sorted-col?
                                                              (if (even? row-index)
                                                                "bg-[#fafbfd]"
                                                                "bg-[#fdfdfe]"))
                                                            "border-[#e7ebee] relative"
                                                            (when resizable-cols? "overflow-visible")
                                                            (when (number? cell) "text-right tabular-nums")
                                                            td-class]
                                                    :style (cond-> {}
                                                             (and col-paths column-layout) (merge (column-layout col-path))
                                                             resizable-cols? (merge (resizable-col-style-fn col-path)))
                                                    :on-mouse-enter (when resizable-cols?
                                                                      (fn [^js event]
                                                                        (when-let [content-el (.querySelector (.-currentTarget event) ".table-resizable-cell-content")]
                                                                          (if (cell-overflowed? content-el)
                                                                            (reset! !hovered-overflow-cell [row-key col-path])
                                                                            (when (= @!hovered-overflow-cell [row-key col-path])
                                                                              (reset! !hovered-overflow-cell nil))))))
                                                    :on-mouse-leave (when resizable-cols?
                                                                      (fn [_]
                                                                        (when (= @!hovered-overflow-cell [row-key col-path])
                                                                          (reset! !hovered-overflow-cell nil))))}
                                                   (apply-sticky-styles {:sticky-col? (and sticky-cols (< i (count sticky-cols)))
                                                                         :col col-path
                                                                         :col-index i}))
                                               ^{:key (str "render-viewer-" cell)}
                                               [:div.table-resizable-cell-content
                                                {:class (cond-> "block whitespace-nowrap"
                                                          resizable-cols? (str " overflow-hidden text-ellipsis")
                                                          expanded-overflow? (str " absolute left-0 top-0 z-30 w-max min-w-full overflow-visible px-2 py-1 -mx-2 -my-1 shadow-sm ring-1 ring-slate-200 bg-inherit"))}
                                                [ui/render-viewer cell]]])))
                                         row))))]
              (concat
               (when (and virtualize? (pos? top-spacer-height))
                 (list [:tr {:aria-hidden true}
                        [:td {:col-span (max 1 (count col-paths))
                              :style {:height (str top-spacer-height "px")
                                      :padding 0
                                      :border "none"}}]]))
               (map-indexed render-row visible-rows)
               (when (and virtualize? (pos? bottom-spacer-height))
                 (list [:tr {:aria-hidden true}
                        [:td {:col-span (max 1 (count col-paths))
                              :style {:height (str bottom-spacer-height "px")
                                      :padding 0
                                      :border "none"}}]])))))
      (when (:footer opts)
        (into [:tfoot.font-bold.border-t-2.print:border-t-black]
              (map (fn [footer-row]
                     ^{:key (str ((:row-key opts hash) footer-row))}
                     (into [:tr.border-b.print:border-b-gray-500.print:hover:bg-transparent
                            {:class ["print:border-b-[1px]"
                                     (when row-action "cursor-pointer")
                                     "hover:bg-indigo-50 odd:bg-[#fbfcfd] even:bg-white border-b-[#e7ebee]"
                                     tr-class]}]
                           (map-indexed (fn [i cell]
                                          [:td.px-2.py-1.border-r.last:border-r-0.whitespace-nowrap
                                           (-> {:class ["text-[12px] print:text-[10px] print:bg-transparent print:px-[5px] print:py-[2px]"
                                                        "border-[#e7ebee]"
                                                        (when (number? cell) "text-right tabular-nums")
                                                        td-class]
                                                :style (cond-> {}
                                                         (and col-paths column-layout) (merge (column-layout (nth col-paths i)))
                                                         resizable-cols? (merge (resizable-col-style-fn (nth col-paths i))))}
                                               (apply-sticky-styles {:sticky-col? (and sticky-cols (< i (count sticky-cols)))
                                                                     :col (nth col-paths i)
                                                                     :col-index i}))
                                           ^{:key (str "render-viewer-" cell)}
                                           [ui/render-viewer cell]]))
                           footer-row)))
              (:footer opts)))]
     (when resizable-cols?
       [:div.absolute.inset-0.pointer-events-none.z-40
        (for [col-path col-paths
              :let [{:keys [x top]} (get @!col-resize-handles col-path)]
              :when (number? x)]
          ^{:key (str "col-resize-overlay-" col-path)}
          [:div.absolute.bottom-0.pointer-events-auto.group
           {:class "w-[14px] -ml-[7px] cursor-col-resize select-none"
            :style {:left (str x "px")
                    :top (str (or top 0) "px")}
            :data-col-resize-handle (pr-str col-path)
            :on-click (fn [^js event]
                        (.preventDefault event)
                        (.stopPropagation event))
            :on-double-click (fn [^js event]
                               (.preventDefault event)
                               (.stopPropagation event)
                               (reset-col-size! col-path))
            :on-mouse-down (fn [^js event]
                             (start-col-resize! event col-path))}
           [:div.absolute.inset-y-0.pointer-events-none
            {:class (cond-> "bg-indigo-500/70 transition-opacity duration-100"
                      true (str " left-1/2 -translate-x-1/2 w-[2px]")
                      (not= @!active-resize-col col-path) (str " opacity-0 group-hover:opacity-100")
                      (= @!active-resize-col col-path) (str " opacity-100"))}]])])
     [:div.fixed.pointer-events-none.border.border-indigo-500
      {:ref !drag-overlay-ref
       :class "bg-indigo-200/20"
       :style {:display "none"
               :left "0px"
               :top "0px"
               :width "0px"
               :height "0px"
               :z-index 70}}]]))
